import { forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useUploadContext } from '../useUploadContext';
import { formattedAllAcceptedFileExtensions } from '~/utilities/fileTypes';

const Input = styled.input`
  display: none;
`;

const ButtonAppearance = styled.label`
  align-items: center;
  background: ${({ theme }) => theme.color.brandBlue500};
  border-radius: 3px;
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-weight: ${({ theme }) => theme.font.weight.regular};
  padding: 9px 18px;
  text-align: center;
`;

const LinkAppearance = styled.label`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.brandBlue500};
  cursor: pointer;
  display: inline-flex;
  font-weight: ${({ theme }) => theme.font.weight.regular};
  padding: 0;
  text-align: center;

  :hover,
  :focus {
    outline: none;
    text-decoration: underline;
  }
`;

const Appearance = forwardRef(({ appearance, props, children }, ref) => {
  if (appearance === 'button') {
    return (
      <ButtonAppearance
        ref={ref}
        {...props} // eslint-disable-line react/jsx-props-no-spreading
      >
        {children}
      </ButtonAppearance>
    );
  }

  if (appearance === 'link') {
    return (
      <LinkAppearance
        ref={ref}
        {...props} // eslint-disable-line react/jsx-props-no-spreading
      >
        {children}
      </LinkAppearance>
    );
  }

  throw new Error('appearance must be either `button` or `link`');
});

export const FilePickerButton = ({
  accept = formattedAllAcceptedFileExtensions,
  appearance = 'button',
  children,
  disabled = false,
  inputId = 1,
  mediaGroupId,
  multiple = true,
  onFilesReceived = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  tabIndex = 0,
  text = 'Upload',
  uploadContainerId,
}) => {
  const { updateQueue } = useUploadContext(uploadContainerId);
  const inputRef = useRef();
  const labelRef = useRef();

  // since we hide the input, we need to hook the focusable label up to keypress and manually
  // click the input for keyboard users
  const onKeypress = (event) => {
    if (['Space', 'Enter'].includes(event.code)) {
      if (document.activeElement === labelRef.current) {
        inputRef.current.click();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keypress', onKeypress);

    return () => {
      document.removeEventListener('keypress', onKeypress);
    };
  }, []);

  const elementProps = {
    ariaControls: 'filename',
    tabIndex,
    htmlFor: inputId,
    onMouseEnter: onHoverIn,
    onFocus: onHoverIn,
    onMouseLeave: onHoverOut,
    onBlur: onHoverOut,
  };

  return (
    <Appearance
      ref={labelRef}
      appearance={appearance}
      props={elementProps}
    >
      <Input
        ref={inputRef}
        multiple={multiple}
        accept={accept}
        id={inputId}
        type="file"
        aria-label="Upload"
        onChange={({ target }) => {
          const files = Array.from(target.files);
          updateQueue(files, { mediaGroupId });
          onFilesReceived();
          inputRef.current.value = '';
        }}
        disabled={disabled}
      />
      {!children && text}
      {children}
    </Appearance>
  );
};

FilePickerButton.propTypes = {
  accept: PropTypes.string,
  appearance: PropTypes.string,
  disabled: PropTypes.bool,
  inputId: PropTypes.string,
  mediaGroupId: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  onFilesReceived: PropTypes.func,
  onHoverIn: PropTypes.func,
  onHoverOut: PropTypes.func,
  tabIndex: PropTypes.number,
  text: PropTypes.string,
  uploadContainerId: PropTypes.string.isRequired,
};
